// FAQ Page - Questions fréquentes
import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  ChevronDown, ChevronUp, Search, HelpCircle, 
  Calendar, CreditCard, Shield, User, Bell, Phone
} from 'lucide-react';

const FAQ = () => {
  const { isDarkMode } = useTheme();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [openItems, setOpenItems] = useState({});

  const faqData = {
    fr: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'Questions Générales', questions: [
          { q: "Qu'est-ce que drbooking ?", a: "drbooking est une plateforme de prise de rendez-vous médicaux en ligne dans 31 pays européens, en 16 langues. Trouvez un professionnel, consultez ses disponibilités et réservez en quelques clics." },
          { q: "drbooking est-il gratuit ?", a: "L'inscription et la recherche sont gratuites. Un abonnement Premium à 5€/mois offre la priorité sur la liste d'attente et des rappels personnalisés." },
          { q: "Dans quels pays drbooking est-il disponible ?", a: "31 pays de l'UE/EEE. Téléconsultation transfrontalière disponible entre tous ces pays." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Rendez-vous', questions: [
          { q: "Comment prendre un rendez-vous ?", a: "3 façons : par recherche manuelle, par assistant vocal IA (24h/24), ou bientôt par WhatsApp. Confirmation par email et SMS." },
          { q: "Puis-je annuler un rendez-vous ?", a: "Oui, jusqu'à 24h avant l'heure prévue depuis votre espace patient." },
          { q: "Qu'est-ce que la liste d'attente intelligente ?", a: "Si aucun créneau n'est disponible, inscrivez-vous sur la liste d'attente. Les Premium sont alertés en premier, les autres 30 minutes plus tard." }
        ]},
        { id: 'payment', icon: CreditCard, title: 'Paiements', questions: [
          { q: "Quels moyens de paiement ?", a: "Cartes bancaires (Visa, Mastercard, Amex) via Stripe. Paiements sécurisés et cryptés." },
          { q: "Comment fonctionne le Premium ?", a: "5€/mois : priorité liste d'attente, rappels personnalisés, support prioritaire. Annulation à tout moment." },
          { q: "Comment obtenir un remboursement ?", a: "Contactez notre support via la page Contact. Traitement sous 48h ouvrées." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Téléconsultation', questions: [
          { q: "Comment fonctionne la téléconsultation ?", a: "Consultez un professionnel en vidéo depuis chez vous. À l'heure du RDV, cliquez sur 'Rejoindre la consultation'. Disponible en 16 langues." },
          { q: "Quels équipements ?", a: "Un appareil avec caméra et microphone, une connexion internet stable, et un navigateur récent (Chrome, Firefox, Safari)." },
          { q: "Est-ce sécurisé ?", a: "Oui, communications cryptées de bout en bout. Données protégées conformément au RGPD et au secret médical." }
        ]},
        { id: 'security', icon: Shield, title: 'Sécurité & RGPD', questions: [
          { q: "Mes données sont-elles protégées ?", a: "drbooking est conforme au RGPD. Données cryptées, jamais partagées sans consentement." },
          { q: "Comment exercer mes droits RGPD ?", a: "Accédez, rectifiez, supprimez ou exportez vos données depuis les Paramètres ou via la page Contact." }
        ]}
      ]
    },
    en: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'General Questions', questions: [
          { q: "What is drbooking?", a: "drbooking is an online medical appointment platform across 31 European countries, in 16 languages. Find a professional, check availability and book in a few clicks." },
          { q: "Is drbooking free?", a: "Registration and search are free. A Premium subscription at €5/month offers priority waiting list and personalized reminders." },
          { q: "In which countries is drbooking available?", a: "31 EU/EEA countries. Cross-border teleconsultation available between all these countries." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Appointments', questions: [
          { q: "How to book an appointment?", a: "3 ways: manual search, AI voice assistant (24/7), or soon via WhatsApp. Confirmation by email and SMS." },
          { q: "Can I cancel an appointment?", a: "Yes, up to 24 hours before the scheduled time from your patient area." },
          { q: "What is the smart waiting list?", a: "If no slot is available, join the waiting list. Premium users are alerted first, others 30 minutes later." }
        ]},
        { id: 'payment', icon: CreditCard, title: 'Payments', questions: [
          { q: "What payment methods?", a: "Bank cards (Visa, Mastercard, Amex) via Stripe. Secure and encrypted payments." },
          { q: "How does Premium work?", a: "€5/month: priority waiting list, personalized reminders, priority support. Cancel anytime." },
          { q: "How to get a refund?", a: "Contact our support via the Contact page. Processing within 48 business hours." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Teleconsultation', questions: [
          { q: "How does teleconsultation work?", a: "Consult a professional via video from home. At appointment time, click 'Join consultation'. Available in 16 languages." },
          { q: "What equipment is needed?", a: "A device with camera and microphone, stable internet connection, and a modern browser (Chrome, Firefox, Safari)." },
          { q: "Is it secure?", a: "Yes, end-to-end encrypted communications. Data protected under GDPR and medical confidentiality." }
        ]},
        { id: 'security', icon: Shield, title: 'Security & GDPR', questions: [
          { q: "Is my data protected?", a: "drbooking is GDPR compliant. Data encrypted, never shared without consent." },
          { q: "How to exercise my GDPR rights?", a: "Access, rectify, delete or export your data from Settings or via the Contact page." }
        ]}
      ]
    },
    de: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'Allgemeine Fragen', questions: [
          { q: "Was ist drbooking?", a: "drbooking ist eine Online-Plattform für Arzttermine in 31 europäischen Ländern, in 16 Sprachen." },
          { q: "Ist drbooking kostenlos?", a: "Registrierung und Suche sind kostenlos. Premium-Abo ab 5€/Monat mit Wartelisten-Priorität." },
          { q: "In welchen Ländern verfügbar?", a: "31 EU/EWR-Länder mit grenzüberschreitender Telekonsultation." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Termine', questions: [
          { q: "Wie buche ich einen Termin?", a: "3 Wege: manuelle Suche, KI-Sprachassistent (24/7), oder bald per WhatsApp." },
          { q: "Kann ich stornieren?", a: "Ja, bis 24 Stunden vor dem Termin." },
          { q: "Was ist die Warteliste?", a: "Premium-Nutzer werden zuerst benachrichtigt, andere 30 Minuten später." }
        ]},
        { id: 'payment', icon: CreditCard, title: 'Zahlungen', questions: [
          { q: "Welche Zahlungsmethoden?", a: "Kreditkarten (Visa, Mastercard, Amex) über Stripe. Sichere Zahlungen." },
          { q: "Wie funktioniert Premium?", a: "5€/Monat: Wartelisten-Priorität, persönliche Erinnerungen. Jederzeit kündbar." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Telekonsultation', questions: [
          { q: "Wie funktioniert die Telekonsultation?", a: "Videokonsultation von zu Hause. In 16 Sprachen verfügbar." },
          { q: "Ist es sicher?", a: "Ja, Ende-zu-Ende-Verschlüsselung. DSGVO-konform." }
        ]},
        { id: 'security', icon: Shield, title: 'Sicherheit & DSGVO', questions: [
          { q: "Sind meine Daten geschützt?", a: "drbooking ist DSGVO-konform. Daten verschlüsselt." },
          { q: "DSGVO-Rechte ausüben?", a: "Daten einsehen, berichtigen, löschen oder exportieren über Einstellungen." }
        ]}
      ]
    },
    it: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'Domande Generali', questions: [
          { q: "Cos'è drbooking?", a: "drbooking è una piattaforma di prenotazione medica online in 31 paesi europei, in 16 lingue." },
          { q: "drbooking è gratuito?", a: "Registrazione e ricerca gratuite. Premium a 5€/mese con lista d'attesa prioritaria." },
          { q: "In quali paesi è disponibile?", a: "31 paesi UE/SEE con teleconsulto transfrontaliero." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Appuntamenti', questions: [
          { q: "Come prenotare?", a: "3 modi: ricerca manuale, assistente vocale IA (24/7), o presto via WhatsApp." },
          { q: "Posso annullare?", a: "Sì, fino a 24 ore prima dell'appuntamento." },
          { q: "Lista d'attesa intelligente?", a: "Premium avvisati per primi, gli altri 30 minuti dopo." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Teleconsulto', questions: [
          { q: "Come funziona il teleconsulto?", a: "Videoconsulto da casa in 16 lingue. Comunicazioni crittografate." },
          { q: "È sicuro?", a: "Sì, crittografia end-to-end. Conforme al GDPR." }
        ]},
        { id: 'security', icon: Shield, title: 'Sicurezza & GDPR', questions: [
          { q: "I miei dati sono protetti?", a: "drbooking è conforme al GDPR. Dati crittografati." }
        ]}
      ]
    },
    bg: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'Общи въпроси', questions: [
          { q: "Какво е drbooking?", a: "drbooking е онлайн платформа за медицински часове в 31 европейски държави, на 16 езика." },
          { q: "drbooking безплатен ли е?", a: "Регистрацията и търсенето са безплатни. Premium абонамент от 5€/месец с приоритет." },
          { q: "В кои държави е наличен?", a: "31 държави от ЕС/ЕИП с трансграничен телеконсулт." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Записване', questions: [
          { q: "Как да запиша час?", a: "3 начина: ръчно търсене, AI гласов асистент (24/7), или скоро чрез WhatsApp." },
          { q: "Мога ли да отменя?", a: "Да, до 24 часа преди записания час." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Телеконсултация', questions: [
          { q: "Как работи телеконсултацията?", a: "Видеоконсултация от вкъщи на 16 езика. Криптирани комуникации." }
        ]},
        { id: 'security', icon: Shield, title: 'Сигурност & GDPR', questions: [
          { q: "Защитени ли са данните ми?", a: "drbooking е съвместим с GDPR. Данните са криптирани." }
        ]}
      ]
    },
    ar: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'أسئلة عامة', questions: [
          { q: "ما هو drbooking؟", a: "drbooking هي منصة لحجز المواعيد الطبية عبر الإنترنت في 31 دولة أوروبية، بـ 16 لغة." },
          { q: "هل drbooking مجاني؟", a: "التسجيل والبحث مجانيان. اشتراك بريميوم بـ 5€/شهر مع أولوية قائمة الانتظار." }
        ]},
        { id: 'booking', icon: Calendar, title: 'المواعيد', questions: [
          { q: "كيف أحجز موعداً؟", a: "3 طرق: البحث اليدوي، المساعد الصوتي الذكي (24/7)، أو قريباً عبر واتساب." },
          { q: "هل يمكنني الإلغاء؟", a: "نعم، حتى 24 ساعة قبل الموعد." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'الاستشارة عن بعد', questions: [
          { q: "كيف تعمل الاستشارة عن بعد؟", a: "استشارة فيديو من المنزل بـ 16 لغة. اتصالات مشفرة." }
        ]},
        { id: 'security', icon: Shield, title: 'الأمان والخصوصية', questions: [
          { q: "هل بياناتي محمية؟", a: "drbooking متوافق مع GDPR. البيانات مشفرة ولا تُشارك بدون موافقة." }
        ]}
      ]
    },
    es: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'Preguntas Generales', questions: [
          { q: "¿Qué es drbooking?", a: "drbooking es una plataforma de citas médicas en línea en 31 países europeos, en 16 idiomas. Encuentre un profesional, consulte disponibilidad y reserve en pocos clics." },
          { q: "¿Es gratuito?", a: "El registro y la búsqueda son gratuitos. Suscripción Premium a 5€/mes con lista de espera prioritaria y recordatorios personalizados." },
          { q: "¿En qué países está disponible?", a: "31 países de la UE/EEE. Teleconsulta transfronteriza disponible entre todos estos países." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Citas', questions: [
          { q: "¿Cómo reservar una cita?", a: "3 formas: búsqueda manual, asistente de voz IA (24/7), o pronto por WhatsApp. Confirmación por email y SMS." },
          { q: "¿Puedo cancelar?", a: "Sí, hasta 24 horas antes de la cita desde su área de paciente." },
          { q: "¿Qué es la lista de espera inteligente?", a: "Si no hay horarios disponibles, únase a la lista de espera. Los Premium son alertados primero, los demás 30 minutos después." }
        ]},
        { id: 'payment', icon: CreditCard, title: 'Pagos', questions: [
          { q: "¿Qué métodos de pago?", a: "Tarjetas bancarias (Visa, Mastercard, Amex) vía Stripe. Pagos seguros y encriptados." },
          { q: "¿Cómo funciona Premium?", a: "5€/mes: prioridad en lista de espera, recordatorios personalizados, soporte prioritario. Cancele en cualquier momento." },
          { q: "¿Cómo obtener un reembolso?", a: "Contacte nuestro soporte vía la página de Contacto. Procesamiento en 48 horas hábiles." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Teleconsulta', questions: [
          { q: "¿Cómo funciona la teleconsulta?", a: "Consulte un profesional por video desde casa. A la hora de la cita, haga clic en 'Unirse a la consulta'. Disponible en 16 idiomas." },
          { q: "¿Qué equipo necesito?", a: "Un dispositivo con cámara y micrófono, conexión estable a internet y un navegador moderno (Chrome, Firefox, Safari)." },
          { q: "¿Es seguro?", a: "Sí, comunicaciones encriptadas de extremo a extremo. Datos protegidos bajo GDPR y secreto médico." }
        ]},
        { id: 'security', icon: Shield, title: 'Seguridad y RGPD', questions: [
          { q: "¿Mis datos están protegidos?", a: "drbooking cumple con el RGPD. Datos encriptados, nunca compartidos sin consentimiento." },
          { q: "¿Cómo ejercer mis derechos RGPD?", a: "Acceda, rectifique, elimine o exporte sus datos desde Configuración o la página de Contacto." }
        ]}
      ]
    },
    pt: {
      categories: [
        { id: 'general', icon: HelpCircle, title: 'Perguntas Gerais', questions: [
          { q: "O que é o drbooking?", a: "drbooking é uma plataforma de marcação de consultas médicas online em 31 países europeus, em 16 idiomas. Encontre um profissional, consulte disponibilidades e marque em poucos cliques." },
          { q: "É gratuito?", a: "O registo e a pesquisa são gratuitos. Subscrição Premium a 5€/mês com lista de espera prioritária e lembretes personalizados." },
          { q: "Em que países está disponível?", a: "31 países da UE/EEE. Teleconsulta transfronteiriça disponível entre todos estes países." }
        ]},
        { id: 'booking', icon: Calendar, title: 'Consultas', questions: [
          { q: "Como marcar uma consulta?", a: "3 formas: pesquisa manual, assistente de voz IA (24/7), ou em breve por WhatsApp. Confirmação por email e SMS." },
          { q: "Posso cancelar?", a: "Sim, até 24 horas antes da consulta a partir da sua área de paciente." },
          { q: "O que é a lista de espera inteligente?", a: "Se não houver horários, inscreva-se na lista de espera. Premium são alertados primeiro, os outros 30 minutos depois." }
        ]},
        { id: 'payment', icon: CreditCard, title: 'Pagamentos', questions: [
          { q: "Que métodos de pagamento?", a: "Cartões bancários (Visa, Mastercard, Amex) via Stripe. Pagamentos seguros e encriptados." },
          { q: "Como funciona o Premium?", a: "5€/mês: prioridade na lista de espera, lembretes personalizados, suporte prioritário. Cancele a qualquer momento." },
          { q: "Como obter reembolso?", a: "Contacte o nosso suporte via a página de Contacto. Processamento em 48 horas úteis." }
        ]},
        { id: 'teleconsultation', icon: Phone, title: 'Teleconsulta', questions: [
          { q: "Como funciona a teleconsulta?", a: "Consulte um profissional por vídeo a partir de casa. Na hora da consulta, clique em 'Entrar na consulta'. Disponível em 16 idiomas." },
          { q: "Que equipamento preciso?", a: "Um dispositivo com câmara e microfone, ligação estável à internet e um navegador moderno (Chrome, Firefox, Safari)." },
          { q: "É seguro?", a: "Sim, comunicações encriptadas ponto a ponto. Dados protegidos sob RGPD e sigilo médico." }
        ]},
        { id: 'security', icon: Shield, title: 'Segurança e RGPD', questions: [
          { q: "Os meus dados estão protegidos?", a: "drbooking cumpre o RGPD. Dados encriptados, nunca partilhados sem consentimento." },
          { q: "Como exercer os meus direitos RGPD?", a: "Aceda, retifique, elimine ou exporte os seus dados nas Definições ou na página de Contacto." }
        ]}
      ]
    }
  };

  // Fallback chain: current language -> English -> French
  const faq = faqData[language] || faqData.en || faqData.fr;
  const categories = faq.categories;

  const toggleItem = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredCategories = categories.map(cat => ({
    ...cat,
    questions: cat.questions.filter(q => 
      q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      q.a.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(cat => cat.questions.length > 0);

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('faq_title')}
          </h1>
          <p className={`text-lg mb-8 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            {t('faq_subtitle')}
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher une question..."
              className={`pl-10 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : ''}`}
            />
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {filteredCategories.map(category => (
            <div key={category.id}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <category.icon className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {category.title}
                </h2>
              </div>

              <div className="space-y-3">
                {category.questions.map((item, idx) => {
                  const isOpen = openItems[`${category.id}-${idx}`];
                  return (
                    <Card 
                      key={idx} 
                      className={`overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white'}`}
                    >
                      <button
                        onClick={() => toggleItem(category.id, idx)}
                        className={`w-full p-4 flex items-center justify-between text-left ${
                          isDarkMode ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {item.q}
                        </span>
                        {isOpen ? (
                          <ChevronUp className={`w-5 h-5 shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                        ) : (
                          <ChevronDown className={`w-5 h-5 shrink-0 ${isDarkMode ? 'text-slate-400' : 'text-gray-400'}`} />
                        )}
                      </button>
                      {isOpen && (
                        <div className={`px-4 pb-4 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <div className={`pt-2 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-100'}`}>
                            {item.a.split('\n').map((line, i) => (
                              <p key={i} className={i > 0 ? 'mt-2' : ''}>{line}</p>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <Card className={`mt-12 ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white'}`}>
          <CardContent className="p-6 text-center">
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Vous n'avez pas trouvé votre réponse ?
            </h3>
            <p className={`mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Notre équipe est là pour vous aider
            </p>
            <Link to="/contact">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Contactez-nous
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FAQ;
